import { Controller } from "@hotwired/stimulus"

// Lazy-loads the AI dashboard summary.
// Only fetches once per page load. The server handles caching (1hr TTL,
// only regenerates on significant activity changes).
export default class extends Controller {
  static targets = ["content", "skeleton", "timestamp"]
  static values = { url: String }

  connect() {
    // Skip if already initialized (Turbo persistence)
    if (this.element.dataset.initialized === "true") return
    this.element.dataset.initialized = "true"
    this.loadSummary()
  }

  async loadSummary() {
    try {
      const response = await fetch(this.urlValue, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()

      // Fade out skeleton first
      if (this.hasSkeletonTarget) {
        this.skeletonTarget.classList.add("opacity-0")
      }

      // After skeleton fades, show content
      setTimeout(() => {
        if (this.hasSkeletonTarget) {
          this.skeletonTarget.classList.add("hidden")
        }
        // Position content and fade in
        this.contentTarget.textContent = data.summary
        this.contentTarget.classList.remove("absolute", "opacity-0")
        this.contentTarget.classList.add("opacity-100")

        // Show timestamp
        if (this.hasTimestampTarget && data.generated_at) {
          const ago = this.timeAgo(new Date(data.generated_at))
          this.timestampTarget.textContent = data.cached ? `Updated ${ago}` : "Just now"
          this.timestampTarget.classList.remove("hidden")
        }
      }, 300)
    } catch (error) {
      console.error("[dashboard-summary]", error)
      if (this.hasSkeletonTarget) {
        this.skeletonTarget.classList.add("opacity-0")
      }
      setTimeout(() => {
        if (this.hasSkeletonTarget) {
          this.skeletonTarget.classList.add("hidden")
        }
        this.contentTarget.textContent = "Welcome back! Check your tasks and notifications below."
        this.contentTarget.classList.remove("absolute", "opacity-0")
        this.contentTarget.classList.add("opacity-100")
      }, 300)
    }
  }

  refresh() {
    // Fade out content
    this.contentTarget.classList.remove("opacity-100")
    this.contentTarget.classList.add("opacity-0", "absolute")
    
    // Show skeleton with fade in
    if (this.hasSkeletonTarget) {
      this.skeletonTarget.classList.remove("hidden", "opacity-0")
    }
    if (this.hasTimestampTarget) {
      this.timestampTarget.classList.add("hidden")
    }
    
    // Allow initialized flag reset for refresh
    this.element.dataset.initialized = "false"
    setTimeout(() => {
      this.element.dataset.initialized = "true"
      this.loadSummary()
    }, 50)
  }

  timeAgo(date) {
    const seconds = Math.floor((new Date() - date) / 1000)
    if (seconds < 60) return "just now"
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
  }
}
